import type { UseControllerProps } from "react-hook-form";

import ResizeTextarea from "react-textarea-autosize";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { TextActionOptional } from "src/features/observations/components/TextActionOptional";
import { ChangeEvent, useEffect, useState } from "react";
import { useDebounce } from "react-use";

interface TextQuestionProps extends UseControllerProps<ObservationFormState> {
	questionNumber: number;

	staticInput?: boolean;

	showAction?: boolean;
}

const TextQuestion = ({
	name,
	rules,
	questionNumber,
	staticInput,
	showAction
}: TextQuestionProps) => {
	const { control } = useFormContext<ObservationFormState>();

	const {
		field: { onChange, value }
	} = useController({
		control,
		name,
		rules
	});

	const { isDisabled } = useWatch({
		control
	});

	const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
		if (staticInput) return;

		setSavedValue(event.target.value);
	};

	const [savedValue, setSavedValue] = useState<string>(value);

	useEffect(() => {
		// We want to include "" values
		// Setting a null value makes the input editable even though it shouldn't
		if (value !== null || value !== undefined) setSavedValue(value);
	}, [value]);

	useDebounce(
		() => {
			onChange(savedValue);
		},
		1000,
		[savedValue]
	);

	return (
		<div>
			<ResizeTextarea
				id={`question-${questionNumber}`}
				placeholder="Please enter your answer"
				className="w-full mt-4 px-3 py-2 bg-white border border-border rounded-md text-base resize-none overflow-hidden focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
				disabled={isDisabled}
				value={savedValue}
				onChange={handleChange}
			/>

			{showAction && (
				<TextActionOptional
					name={`questions.${questionNumber - 1}.action`}
					questionNumber={questionNumber}
					staticInput={staticInput}
				/>
			)}
		</div>
	);
};

export { TextQuestion, type TextQuestionProps };
